import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  TrendingUp,
  Target,
  FileText,
  Scan
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard({ user, logout, token }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, scansRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/scans`, { headers, withCredentials: true })
      ]);
      setStats(statsRes.data);
      setScans(scansRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "bg-red-500",
      high: "bg-orange-500",
      medium: "bg-yellow-500",
      low: "bg-blue-500",
      info: "bg-slate-500"
    };
    return colors[severity] || colors.info;
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: { variant: "default", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
      running: { variant: "default", className: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
      pending: { variant: "default", className: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" },
      failed: { variant: "destructive", className: "" }
    };
    return variants[status] || variants.pending;
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-[Chivo]">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}
            </h2>
            <p className="text-muted-foreground">
              Here's your security overview for today
            </p>
          </div>
          <Button 
            className="glow-primary" 
            onClick={() => navigate('/scan/new')}
            data-testid="dashboard-new-scan-btn"
          >
            <Scan className="h-4 w-4 mr-2" />
            New Security Scan
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
          >
            <Card className="bg-card border-border card-hover" data-testid="stat-total-scans">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Scans</p>
                    <p className="text-3xl font-bold font-[Chivo]">{stats?.total_scans || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card border-border card-hover" data-testid="stat-vulnerabilities">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Vulnerabilities</p>
                    <p className="text-3xl font-bold font-[Chivo]">{stats?.total_vulnerabilities || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-sm bg-orange-500/10 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card border-border card-hover" data-testid="stat-critical">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Critical Issues</p>
                    <p className="text-3xl font-bold font-[Chivo] text-red-500">
                      {stats?.critical_vulnerabilities || 0}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-border card-hover" data-testid="stat-risk-score">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Risk Score</p>
                    <p className="text-3xl font-bold font-[Chivo]">{stats?.average_risk_score || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Recent Scans */}
        <Card className="bg-card border-border" data-testid="recent-scans-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-[Chivo]">Recent Scans</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/scan/new')}>
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {scans.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No scans yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start your first security assessment to see results here
                </p>
                <Button onClick={() => navigate('/scan/new')} data-testid="empty-state-new-scan-btn">
                  <Scan className="h-4 w-4 mr-2" />
                  Start First Scan
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {scans.slice(0, 5).map((scan, index) => (
                  <motion.div
                    key={scan.scan_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-4 border border-border rounded-sm hover:border-primary/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/scan/${scan.scan_id}`)}
                    data-testid={`scan-item-${scan.scan_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                        scan.status === 'completed' ? 'bg-emerald-500/10' : 
                        scan.status === 'running' ? 'bg-blue-500/10' : 'bg-yellow-500/10'
                      }`}>
                        {scan.status === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-emerald-500" />
                        ) : scan.status === 'running' ? (
                          <Clock className="h-5 w-5 text-blue-500 animate-pulse" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{scan.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="capitalize">{scan.config.scan_type}</span>
                          <span>•</span>
                          <span>{new Date(scan.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {scan.status === 'running' && (
                        <div className="w-24">
                          <Progress value={scan.progress} className="h-2" />
                          <p className="text-xs text-muted-foreground mt-1">{scan.progress}%</p>
                        </div>
                      )}
                      {scan.status === 'completed' && scan.summary && (
                        <div className="flex items-center gap-2">
                          {Object.entries(scan.summary.severity_breakdown || {}).map(([severity, count]) => (
                            count > 0 && (
                              <div key={severity} className="flex items-center gap-1">
                                <div className={`h-2 w-2 rounded-full ${getSeverityColor(severity)}`} />
                                <span className="text-xs text-muted-foreground">{count}</span>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                      <Badge className={getStatusBadge(scan.status).className}>
                        {scan.status}
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate('/scan/new')} data-testid="quick-action-scan">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Scan className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Run Security Scan</p>
                  <p className="text-sm text-muted-foreground">Start a new vulnerability assessment</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate('/compliance')} data-testid="quick-action-compliance">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-500" />
                </div>
                <div>
                  <p className="font-medium">Compliance Center</p>
                  <p className="text-sm text-muted-foreground">View HIPAA & compliance status</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate('/reports')} data-testid="quick-action-reports">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Generate Reports</p>
                  <p className="text-sm text-muted-foreground">Create PDF & DOCX reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
